<script setup>
import ECommerceCategoryContentTable from '@frontJs/components/tables/projectManagement/seo/ECommerceCategoryContentTable.vue';
import TenantReportECommerceCategoryContentTable from '@frontJs/components/tables/projectManagement/seo/tenantReports/ECommerceCategoryContentTable.vue';
import ProjectManagementLayout from '@frontJs/layouts/ProjectManagementLayout.vue';
import { Head } from '@inertiajs/vue3';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';
import { useRoute } from '@js/utils/route';
import { h, onMounted, ref } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: [Object, null],
        required: false,
    },
});

const { route } = useRoute();

const loading = ref(true);

const data = ref({
    all: 0,
    withoutDescription: 0,
    withoutMetaTitle: 0,
    withoutMetaDescription: 0,
    updated: 0,
    standard: 0,
    premium: 0,
    useless: 0,
});

function updateData() {
    if (tenant) {
        loading.value = true;

        Promise.all([
            axios.post(
                route(
                    'front.projectManagement.seo.postECommerceCategoryContentsCount',
                    { tenant: tenant.id },
                ),
                {
                    hideNotDoneSeoApikomContent: true,
                },
            ),
            axios.post(
                route(
                    'front.projectManagement.seo.postECommerceCategoryContentsCount',
                    { tenant: tenant.id },
                ),
                {
                    withoutDescription: true,
                    hideNotDoneSeoApikomContent: true,
                },
            ),
            axios.post(
                route(
                    'front.projectManagement.seo.postECommerceCategoryContentsCount',
                    { tenant: tenant.id },
                ),
                {
                    withoutMetaTitle: true,
                    hideNotDoneSeoApikomContent: true,
                },
            ),
            axios.post(
                route(
                    'front.projectManagement.seo.postECommerceCategoryContentsCount',
                    { tenant: tenant.id },
                ),
                {
                    withoutMetaDescription: true,
                    hideNotDoneSeoApikomContent: true,
                },
            ),
            axios.post(
                route(
                    'front.projectManagement.seo.postECommerceCategoryContentsCount',
                    { tenant: tenant.id },
                ),
                {
                    updated: true,
                    hideNotDoneSeoApikomContent: true,
                },
            ),
            axios.post(
                route(
                    'front.projectManagement.seo.postECommerceCategoryContentsCount',
                    { tenant: tenant.id },
                ),
                {
                    standard: true,
                    hideNotDoneSeoApikomContent: true,
                },
            ),
            axios.post(
                route(
                    'front.projectManagement.seo.postECommerceCategoryContentsCount',
                    { tenant: tenant.id },
                ),
                {
                    premium: true,
                    hideNotDoneSeoApikomContent: true,
                },
            ),
            axios.post(
                route(
                    'front.projectManagement.seo.postECommerceCategoryContentsCount',
                    { tenant: tenant.id },
                ),
                {
                    useless: true,
                    hideNotDoneSeoApikomContent: true,
                },
            ),
        ])
            .then(
                ([
                    allData,
                    withoutDescriptionData,
                    withoutMetaTitleData,
                    withoutMetaDescriptionData,
                    updatedData,
                    standardData,
                    premiumData,
                    uselessData,
                ]) => {
                    data.value.all = allData.data.count;
                    data.value.withoutDescription =
                        withoutDescriptionData.data.count;
                    data.value.withoutMetaTitle =
                        withoutMetaTitleData.data.count;
                    data.value.withoutMetaDescription =
                        withoutMetaDescriptionData.data.count;
                    data.value.updated = updatedData.data.count;
                    data.value.standard = standardData.data.count;
                    data.value.premium = premiumData.data.count;
                    data.value.useless = uselessData.data.count;
                },
            )
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                loading.value = false;
            });
    }
}

onMounted(() => {
    if (tenant) {
        updateData();
    }
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
    <Head>
        <title>
            Contenus catégories - SEO/GEO - Gestion de projet - Datakepler
        </title>
    </Head>

    <PageTitle :tenant-name="tenant ? tenant.name : undefined">
        Gestion de projet - SEO/GEO - Contenus catégories
    </PageTitle>

    <TabLayout
        v-if="tenant"
        :tabs="{
            all: {
                title: 'Tous',
                total: data.all,
                content: h(ECommerceCategoryContentTable, {
                    tenant,
                    hideNotDoneSeoApikomContent: true,
                }),
            },
            missing: {
                title: 'À compléter',
                items: {
                    withoutDescription: {
                        title: 'Sans description',
                        total: data.withoutDescription,
                        content: h(ECommerceCategoryContentTable, {
                            tenant,
                            withoutDescription: true,
                            hideNotDoneSeoApikomContent: true,
                        }),
                    },
                    withoutMetaTitle: {
                        title: 'Sans méta-titre',
                        total: data.withoutMetaTitle,
                        content: h(ECommerceCategoryContentTable, {
                            tenant,
                            withoutMetaTitle: true,
                            hideNotDoneSeoApikomContent: true,
                        }),
                    },
                    withoutMetaDescription: {
                        title: 'Sans méta-description',
                        total: data.withoutMetaDescription,
                        content: h(ECommerceCategoryContentTable, {
                            tenant,
                            withoutMetaDescription: true,
                            hideNotDoneSeoApikomContent: true,
                        }),
                    },
                },
            },
            updated: {
                title: 'Mis à jour',
                total: data.updated,
                content: h(ECommerceCategoryContentTable, {
                    tenant,
                    updated: true,
                    hideNotDoneSeoApikomContent: true,
                }),
            },
            standard: {
                title: 'Standard',
                total: data.standard,
                content: h(ECommerceCategoryContentTable, {
                    tenant,
                    standard: true,
                    hideNotDoneSeoApikomContent: true,
                }),
            },
            premium: {
                title: 'Premium',
                total: data.premium,
                content: h(ECommerceCategoryContentTable, {
                    tenant,
                    premium: true,
                    hideNotDoneSeoApikomContent: true,
                }),
            },
            useless: {
                title: 'Inutiles',
                total: data.useless,
                content: h(ECommerceCategoryContentTable, {
                    tenant,
                    useless: true,
                    hideNotDoneSeoApikomContent: true,
                }),
            },
        }"
        :loading="loading"
    />

    <TenantReportECommerceCategoryContentTable v-else />
</template>
